import { useUserProfile } from '@/context/UserProfileContext';
import { api } from '@/utils/api';
import { mediumImpact } from '@/utils/capacitorUtils';
import { supabase, supabaseWithOfflineSupport } from '@/utils/supabase';
import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonToolbar,
  useIonToast
} from '@ionic/react';
import { arrowBackOutline } from 'ionicons/icons';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FairwayToggle from './FairwayToggle';
import FooterActions from './FooterActions';
import PenaltyInputs from './PenaltyInputs';
import PlayerSelector from './PlayerSelector';
import ScoreInputs from './ScoreInputs';
import { BaseScoreProps, PlayerInfo, ScoreData } from './types';

interface ScoreViewProps extends BaseScoreProps {
  players: PlayerInfo[];
  initialActivePlayer: PlayerInfo | null;
  onClose: () => void;
  sheetMode?: boolean;
  initialScoreData?: Record<string, Record<number, ScoreData>>;
}

export interface ScoreViewRef {
  saveScore: () => Promise<void>;
}

async function saveScoreForPlayer({
  gameId,
  teeId,
  holeNumber,
  playerId,
  scoreData,
  profile,
  setOfflineQueue
}: {
  gameId: string;
  teeId: string;
  holeNumber: number;
  playerId: string;
  scoreData: ScoreData;
  profile: any;
  setOfflineQueue: React.Dispatch<React.SetStateAction<any[]>>;
}) {
  const scoreRecord = {
    game_id: parseInt(gameId),
    player_id: playerId,
    tee_id: teeId,
    hole_number: holeNumber,
    score: scoreData.score,
    putts: scoreData.putts,
    water_penalties: scoreData.waterPenalties,
    ob_penalties: scoreData.obPenalties,
    drop_penalties: scoreData.dropPenalties,
    fairway_hit: scoreData.fairwayHit,
    entered_by: profile?.userId
  };
  const condition = {
    game_id: parseInt(gameId),
    player_id: playerId,
    hole_number: holeNumber
  };
  try {
    const { error } = await supabaseWithOfflineSupport.upsertScore('game_scores', scoreRecord, condition);
    if (error) {
      if (!navigator.onLine || (typeof error === 'object' && error && 'message' in error && typeof error.message === 'string' && error.message.includes('network'))) {
        // Add to offline queue
        const queueItem = {
          timestamp: Date.now(),
          gameId,
          playerId,
          holeNumber,
          scoreData,
          enteredBy: profile?.userId
        };
        setOfflineQueue(prev => [...prev, queueItem]);
        try {
          const existingQueue = JSON.parse(localStorage.getItem('mgp_offline_scores') || '[]');
          localStorage.setItem('mgp_offline_scores', JSON.stringify([...existingQueue, queueItem]));
        } catch (e) {
          // ignore
        }
      } else {
        throw error;
      }
    }
  } catch (error) {
    presentToast({
      message: 'Error saving score',
      duration: 2000,
      color: 'danger'
    });
  }
}

const ScoreView = forwardRef<ScoreViewRef, ScoreViewProps>((props, ref) => {
  const {
    gameId,
    teeId,
    holeNumber,
    par,
    players = [],
    initialActivePlayer,
    onSaveScore,
    onClose,
    sheetMode = false,
    initialScoreData
  } = props;
  const { t } = useTranslation();
  const { profile } = useUserProfile();

  const [playerScores, setPlayerScores] = useState<Record<string, Record<number, ScoreData>>>(
    initialScoreData || {}
  );
  const [activePlayer, setActivePlayer] = useState<PlayerInfo>(initialActivePlayer || players[0]);
  const [isSaving, setIsSaving] = useState(false);
  const [presentToast] = useIonToast();
  const [offlineQueue, setOfflineQueue] = useState<any[]>([]);

  // Score fields state
  const [score, setScore] = useState<number | null>(null);
  const [putts, setPutts] = useState<number | null>(null);
  const [waterPenalties, setWaterPenalties] = useState(0);
  const [obPenalties, setObPenalties] = useState(0);
  const [dropPenalties, setDropPenalties] = useState(0);
  const [fairwayHit, setFairwayHit] = useState<'left' | 'center' | 'right' | 'missed' | undefined>(undefined);

  // Always load the score fields from playerScores when activePlayer or holeNumber changes
  useEffect(() => {
    const data = playerScores[activePlayer.userId]?.[holeNumber];
    setScore(data?.score ?? null);
    setPutts(data?.putts ?? null);
    setWaterPenalties(data?.waterPenalties ?? 0);
    setObPenalties(data?.obPenalties ?? 0);
    setDropPenalties(data?.dropPenalties ?? 0);
    setFairwayHit(data?.fairwayHit ?? undefined);
  }, [activePlayer, holeNumber, playerScores]);

  // Process offline queue
  const processOfflineQueue = async () => {
    try {
      const offlineScoresStr = localStorage.getItem('mgp_offline_scores');
      if (!offlineScoresStr) return;
      const offlineScores = JSON.parse(offlineScoresStr);
      if (!offlineScores.length) return;
      setOfflineQueue(offlineScores);
      const newQueue = [];
      for (const item of offlineScores) {
        try {
          if (item.gameId !== gameId) {
            newQueue.push(item);
            continue;
          }
          await api.post(`/game/${item.gameId}/scores`, {
            playerId: item.playerId,
            holeNumber: item.holeNumber,
            ...item.scoreData
          });
        } catch (error: any) {
          if (!navigator.onLine || (error.message && error.message.includes('network'))) {
            newQueue.push(item);
          }
        }
      }
      localStorage.setItem('mgp_offline_scores', JSON.stringify(newQueue));
      setOfflineQueue(newQueue);
      if (offlineScores.length !== newQueue.length) {
        presentToast({
          message: t('scores_synced'),
          duration: 2000,
          position: 'bottom',
          color: 'success'
        });
      }
    } catch (e) {
      // ignore
    }
  };

  // On mount, process offline queue
  useEffect(() => {
    try {
      const storedQueue = localStorage.getItem('mgp_offline_scores');
      if (storedQueue) {
        const parsedQueue = JSON.parse(storedQueue);
        setOfflineQueue(parsedQueue);
      }
    } catch (e) { }
    if (navigator.onLine && offlineQueue.length > 0) {
      const timeoutId = setTimeout(() => {
        processOfflineQueue();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  // Load initial scores from backend
  useEffect(() => {
    if (!gameId) return;
    let isMounted = true;
    const loadScores = async () => {
      try {
        const { data, error } = await supabase
          .from('game_scores')
          .select('*')
          .eq('game_id', gameId);
        if (error) return;
        if (isMounted && data && data.length > 0) {
          const scoreMap: Record<string, Record<number, ScoreData>> = {};
          data.forEach((scoreRecord: any) => {
            const {
              player_id,
              hole_number,
              score,
              putts,
              water_penalties,
              ob_penalties,
              drop_penalties,
              fairway_hit
            } = scoreRecord;
            if (!scoreMap[player_id]) scoreMap[player_id] = {};
            scoreMap[player_id][hole_number] = {
              score,
              putts,
              waterPenalties: water_penalties,
              obPenalties: ob_penalties,
              dropPenalties: drop_penalties,
              fairwayHit: fairway_hit
            };
          });
          setPlayerScores(prevScores => ({ ...prevScores, ...scoreMap }));
        }
      } catch (error) { }
    };
    loadScores();
    return () => { isMounted = false; };
  }, [gameId]);

  // Supabase realtime: Keep local state in sync with backend
  useEffect(() => {
    if (!gameId) return;
    const channelName = `game-scores-${gameId}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'game_scores',
        filter: `game_id=eq.${gameId}`,
      }, (payload: any) => {
        if (payload.new) {
          const {
            player_id,
            hole_number,
            score,
            putts,
            water_penalties,
            ob_penalties,
            drop_penalties,
            fairway_hit
          } = payload.new as any;
          setPlayerScores(prev => {
            const playerMap = { ...prev };
            if (!playerMap[player_id]) playerMap[player_id] = {};
            playerMap[player_id][hole_number] = {
              score,
              putts,
              waterPenalties: water_penalties,
              obPenalties: ob_penalties,
              dropPenalties: drop_penalties,
              fairwayHit: fairway_hit
            };
            return playerMap;
          });
        }
      })
      .subscribe();
    return () => { channel.unsubscribe(); };
  }, [gameId]);

  // Save to backend (and local state) every time any score field changes
  const handleFieldChange = (field: keyof ScoreData, value: any) => {
    let newScore = score;
    let newPutts = putts;
    let newWaterPenalties = waterPenalties;
    let newObPenalties = obPenalties;
    let newDropPenalties = dropPenalties;
    let newFairwayHit = fairwayHit;

    switch (field) {
      case 'score': newScore = value; break;
      case 'putts': newPutts = value; break;
      case 'waterPenalties': newWaterPenalties = value; break;
      case 'obPenalties': newObPenalties = value; break;
      case 'dropPenalties': newDropPenalties = value; break;
      case 'fairwayHit': newFairwayHit = value; break;
    }

    // Golf rule: Score must always be at least putts + 1
    // If we're updating putts and it would equal or exceed the score, bump the score
    if (field === 'putts' && newScore !== null && newPutts !== null && newPutts >= newScore) {
      newScore = newPutts + 1;
    }

    setScore(newScore);
    setPutts(newPutts);
    setWaterPenalties(newWaterPenalties);
    setObPenalties(newObPenalties);
    setDropPenalties(newDropPenalties);
    setFairwayHit(newFairwayHit);

    // Always save to backend (immediately, optimistic)
    saveScoreForPlayer({
      gameId,
      teeId,
      holeNumber,
      playerId: activePlayer.userId,
      scoreData: {
        score: newScore ?? par,
        putts: newPutts ?? 2,
        waterPenalties: newWaterPenalties,
        obPenalties: newObPenalties,
        dropPenalties: newDropPenalties,
        fairwayHit: newFairwayHit ?? 'center'
      },
      profile,
      setOfflineQueue
    });
  };

  // When switching players, flush current fields and load new
  const switchToPlayer = async (playerInfo: PlayerInfo) => {
    mediumImpact();
    // flush current player's score (again, to be sure)
    await saveScoreForPlayer({
      gameId,
      teeId,
      holeNumber,
      playerId: activePlayer.userId,
      scoreData: {
        score: score ?? par,
        putts: putts ?? 2,
        waterPenalties,
        obPenalties,
        dropPenalties,
        fairwayHit: fairwayHit ?? 'center'
      },
      profile,
      setOfflineQueue,
    });
    setActivePlayer(playerInfo);
  };

  // Expose the saveScore method via ref (if used externally)
  useImperativeHandle(ref, () => ({
    saveScore: async () => {
      setIsSaving(true);
      await saveScoreForPlayer({
        gameId,
        teeId,
        holeNumber,
        playerId: activePlayer.userId,
        scoreData: {
          score: score ?? par,
          putts: putts ?? 2,
          waterPenalties,
          obPenalties,
          dropPenalties,
          fairwayHit: fairwayHit ?? 'center'
        },
        profile,
        setOfflineQueue,
      });
      setIsSaving(false);
      if (onSaveScore) {
        onSaveScore(playerScores[activePlayer.userId]?.[holeNumber], activePlayer.id);
      }
    }
  }));

  return (
    <>
      {!sheetMode && (
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={onClose}>
                <IonIcon slot="icon-only" icon={arrowBackOutline} />
              </IonButton>
            </IonButtons>
            <div className="text-center font-bold">
              {t('hole')} {holeNumber} - Par {par}
            </div>
          </IonToolbar>
        </IonHeader>
      )}

      <div className="ion-padding space-y-4">
        {/* Player selection */}
        {players && players.length > 1 && (
          <div className="grid grid-cols-4 gap-2 overflow-x-auto mb-2">
            {players.map(player => (
              <PlayerSelector
                key={player.id}
                player={player}
                activePlayer={activePlayer}
                switchToPlayer={switchToPlayer}
              />
            ))}
          </div>
        )}

        <ScoreInputs
          score={score}
          putts={putts}
          par={par}
          onScoreChange={v => handleFieldChange('score', v)}
          onPuttsChange={v => handleFieldChange('putts', v)}
          onScoreInteraction={() => { }}
          onPuttsInteraction={() => { }}
        />

        <PenaltyInputs
          waterPenalties={waterPenalties}
          obPenalties={obPenalties}
          dropPenalties={dropPenalties}
          onWaterPenaltiesChange={v => handleFieldChange('waterPenalties', v)}
          onObPenaltiesChange={v => handleFieldChange('obPenalties', v)}
          onDropPenaltiesChange={v => handleFieldChange('dropPenalties', v)}
        />

        <FairwayToggle
          value={fairwayHit}
          onChange={v => handleFieldChange('fairwayHit', v)}
        />

        {/* Save button for sheet mode */}
        {sheetMode && (
          <IonButton
            size="large"
            fill="solid"
            shape='round'
            color="secondary"
            onClick={async () => {
              if (onSaveScore) {
                onSaveScore(playerScores[activePlayer.userId]?.[holeNumber], activePlayer.id);
              }
            }}
            expand="block"
            className="w-full"
            disabled={isSaving}
          >
            <div className="flex items-center justify-center">
              <span className="text-sm font-bold">{t('close')}</span>
            </div>
          </IonButton>
        )}
      </div>
      {!sheetMode && (
        <FooterActions
          onSave={async () => {
            setIsSaving(true);
            await saveScoreForPlayer({
              gameId,
              teeId,
              holeNumber,
              playerId: activePlayer.userId,
              scoreData: {
                score: score ?? par,
                putts: putts ?? 2,
                waterPenalties,
                obPenalties,
                dropPenalties,
                fairwayHit: fairwayHit ?? 'center'
              },
              profile,
              setOfflineQueue
            });
            setIsSaving(false);
            if (onSaveScore) {
              onSaveScore(playerScores[activePlayer.userId]?.[holeNumber], activePlayer.id);
            }
          }}
          onClose={onClose}
          isSaving={isSaving}
        />
      )}
    </>
  );
});

export default ScoreView;